<template>
	<div
		v-if="disabled"
		v-tooltip="PENDING_GUIDANCE"
		class="flex w-full items-center justify-between border-b border-won-gray-light py-3 pr-5 pl-4 opacity-50"
	>
		<span class="flex items-center gap-0.5">
			<ChevronGray size-class="h-3 w-3" />

			<span class="text-[14px] font-medium text-won-black">{{ title }}</span>
		</span>

		<span class="rounded bg-won-sand-light px-2 py-0.5 text-[12px] font-medium text-won-secondary">
			{{ current }}/{{ max }}
		</span>
	</div>

	<Disclosure
		v-else
		v-slot="{ open }"
		as="div"
		:default-open="defaultOpen"
		class="w-full border-b border-won-gray-light"
	>
		<DisclosureButton
			as="div"
			class="flex w-full cursor-pointer items-center justify-between py-3 pr-5 pl-4"
		>
			<span class="flex items-center gap-0.5">
				<ChevronGray
					:class="['transition-transform', open ? 'rotate-90' : '']"
					size-class="h-3 w-3"
				/>

				<span class="text-[14px] font-medium text-won-black">{{ title }}</span>
			</span>

			<span class="rounded bg-won-sand-light px-2 py-0.5 text-[12px] font-medium text-won-secondary">
				{{ current }}/{{ max }}
			</span>
		</DisclosureButton>

		<DisclosurePanel class="flex flex-col gap-1 pr-5 pb-3 pl-4">
			<slot v-if="hasItems" />

			<p
				v-else-if="emptyMessage"
				class="rounded-md border border-won-gray-light bg-won-sand-light p-2 text-[14px] text-won-secondary"
			>
				{{ emptyMessage }}
			</p>

			<div
				v-else-if="lockedCount === 0"
				class="flex flex-col items-center gap-1 rounded-md border border-won-gray-light bg-won-sand-light p-2"
			>
				<CheckCircleIcon class="size-5 text-won-green" />

				<p class="text-[12px] text-won-secondary">
					Looks good. No fixes needed here
				</p>
			</div>

			<button
				v-if="lockedCount > 0"
				type="button"
				class="
					flex w-full cursor-pointer items-center justify-center gap-2 rounded border border-won-green p-[7px]
					text-[14px] font-medium text-won-green"
				@click="emit('upgrade')"
			>
				<LockOpenIcon class="size-5" />
				View {{ lockedCount }} More {{ lockedCount === 1 ? 'Fix' : 'Fixes' }}
			</button>
		</DisclosurePanel>
	</Disclosure>
</template>

<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import { CheckCircleIcon, LockOpenIcon } from '@heroicons/vue/24/outline';
import ChevronGray from '../../icons/chevron-gray.vue';

// EN-812: shown on hover while the resume is pending. Like everything in the panel, it says what
// unlocks scoring, never that anything is limited.
const PENDING_GUIDANCE = 'Add your education or work experience to start scoring and get recommendations.';

defineProps({
	title: {
		type: String,
		required: true,
	},
	current: {
		type: Number,
		required: true,
	},
	max: {
		type: Number,
		required: true,
	},
	defaultOpen: {
		type: Boolean,
		default: false,
	},
	// Whether the slot will render anything: cards, or Target Alignment's role states. Without
	// any, the category says it looks good (EN-812) rather than leaving a blank space (EN-936).
	hasItems: {
		type: Boolean,
		default: true,
	},
	// EN-812: fixes detected but not revealed to this account, counted on the one button that
	// opens the upgrade prompt.
	lockedCount: {
		type: Number,
		default: 0,
	},
	// Shown in place of the clean state when having no cards doesn't mean the category is fine —
	// Content Strength on a resume with no real bullets yet.
	emptyMessage: {
		type: String,
		default: null,
	},
	// EN-812: the resume is pending — not enough real content to score yet — so every category
	// is inert (non-expandable, muted), with guidance on hover.
	disabled: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['upgrade']);
</script>
